import { useState, useRef } from 'react';
import api from '@/lib/api';
import { useConfirmation } from './shared/ZCard';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Globe, Camera, Flame, ListTodo, Zap, Upload, X, Check, ArrowRight, FileText, Image, Loader2, Mic, Sparkles } from 'lucide-react';

const APP_META = {
  giraffe:  { icon: Globe, label: 'Giraffe', color: '#457b9d', path: '/giraffe' },
  framed:   { icon: Camera, label: 'Framed', color: '#9B6DD4', path: '/framed' },
  cult:     { icon: Flame, label: 'Cult of One', color: '#f4a261', path: '/cult' },
  uncode:   { icon: ListTodo, label: 'Uncode', color: '#52b788', path: '/uncode' },
  soulsync: { icon: Sparkles, label: 'SoulSync', color: '#4B5FD4', path: '/soulsync' },
};

const FILE_ICONS = {
  image: Image,
  pdf: FileText,
  doc: FileText,
  default: FileText,
};

function getFileType(file) {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type.includes('document') || file.type.includes('word')) return 'doc';
  return 'default';
}

export default function UniversalInput({ onClose, initialText = '', initialApp = null, initialTripId = null }) {
  const feedback = useConfirmation();
  const navigate = useNavigate();
  const [text, setText] = useState(initialText);
  const [files, setFiles] = useState([]); // { file, preview, base64, type, status: 'pending'|'processing'|'done'|'error', classification }
  const [step, setStep] = useState('input'); // 'input' | 'processing' | 'confirm'
  const [classifications, setClassifications] = useState([]); // Array of classification results
  const [applying, setApplying] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const fileRef = useRef(null);
  const textareaRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const resizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxPx = Math.round(window.innerHeight * 0.4);
    el.style.height = Math.min(el.scrollHeight, maxPx) + 'px';
  };

  const startRecording = async () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        setRecording(false);
        setTranscribing(true);
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const ext = mimeType === 'audio/mp4' ? 'mp4' : 'webm';
        const formData = new FormData();
        formData.append('file', blob, `recording.${ext}`);
        try {
          const { data } = await api.post('/tasks/transcribe-voice', formData);
          const transcribed = data.body || data.text || '';
          if (transcribed) {
            setText(prev => prev.trim() ? prev + '\n' + transcribed : transcribed);
            requestAnimationFrame(resizeTextarea);
          }
        } catch {
          feedback.fail('Transcription failed');
        } finally {
          setTranscribing(false);
          streamRef.current?.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }
      };
      recorder.start();
      setRecording(true);
    } catch {
      feedback.fail('Could not access microphone');
    }
  };

  const processFile = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const base64 = ev.target.result.split(',')[1];
        const type = getFileType(file);
        resolve({
          file,
          name: file.name,
          preview: type === 'image' ? ev.target.result : null,
          base64,
          type,
          status: 'pending',
          classification: null,
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFiles = async (fileList) => {
    const newFiles = await Promise.all(
      Array.from(fileList).map(processFile)
    );
    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files?.length) {
      handleFiles(e.target.files);
    }
  };

  // Handle paste event for clipboard files
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const pastedFiles = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          pastedFiles.push(file);
        }
      }
    }

    if (pastedFiles.length > 0) {
      e.preventDefault();
      handleFiles(pastedFiles);
      feedback.confirm(`${pastedFiles.length} file${pastedFiles.length > 1 ? 's' : ''} pasted`);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!text && files.length === 0) return;
    setStep('processing');

    try {
      // Process all files + text in parallel
      const inputs = [];
      
      // Add text input if present
      if (text.trim()) {
        inputs.push({ text, image_base64: '', index: -1 });
      }
      
      // Add each file
      files.forEach((f, i) => {
        if (f.type === 'image') {
          inputs.push({ text: '', image_base64: f.base64, index: i });
        } else {
          // For PDFs/docs, send as text extraction request
          inputs.push({ text: `[FILE: ${f.name}]`, file_base64: f.base64, file_type: f.type, index: i });
        }
      });

      // Process all inputs
      const results = await Promise.all(
        inputs.map(async (input) => {
          try {
            const payload = { text: input.text };
            if (initialApp) payload.hint_app = initialApp;
            if (initialTripId) payload.hint_trip_id = initialTripId;
            if (input.image_base64) payload.image_base64 = input.image_base64;
            if (input.file_base64) {
              payload.file_base64 = input.file_base64;
              payload.file_type = input.file_type;
            }
            const { data } = await api.post('/universal-input', payload);
            return { ...data.classification, _index: input.index, _success: true };
          } catch (err) {
            return { _index: input.index, _success: false, _error: err.message };
          }
        })
      );

      // Update file statuses
      const successfulResults = results.filter(r => r._success);
      setClassifications(successfulResults);
      
      if (successfulResults.length === 0) {
        feedback.fail('Failed to process inputs');
        setStep('input');
      } else {
        setStep('confirm');
      }
    } catch (err) {
      feedback.fail('Processing failed');
      setStep('input');
    }
  };

  const handleAcceptAll = async () => {
    setApplying(true);
    let successCount = 0;
    
    try {
      for (const classification of classifications) {
        try {
          await api.post('/universal-input/apply', {
            app: classification.app,
            action: classification.action || 'create',
            target_id: classification.target_id,
            trip_id: classification.trip_id,
            data: classification.data,
          });
          
          window.dispatchEvent(new CustomEvent('universal-input-applied', {
            detail: { app: classification.app, action: classification.action, trip_id: classification.trip_id }
          }));
          successCount++;
        } catch (err) {
          console.error('Failed to apply:', err);
        }
      }
      
      if (successCount > 0) {
        feedback.confirm(`${successCount} item${successCount > 1 ? 's' : ''} added`);
      }
      onClose();
    } catch (err) {
      feedback.fail('Failed to save');
    } finally {
      setApplying(false);
    }
  };

  const handleChangeApp = (index, newApp) => {
    setClassifications(prev => prev.map((c, i) => i === index ? { ...c, app: newApp } : c));
  };

  const removeClassification = (index) => {
    setClassifications(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5] max-w-lg p-0 overflow-hidden max-h-[85vh]" aria-describedby="universal-input-desc">
        {/* Input step */}
        {step === 'input' && (
          <div className="p-5" data-testid="universal-input-step">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded bg-[#6B35B8]/20 flex items-center justify-center">
                <Zap size={14} className="text-[#9B6DD4]" />
              </div>
              <span className="text-sm font-medium text-[#f0f0f5]">What's on your mind?</span>
            </div>

            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              className={`border-2 border-dashed rounded-xl p-4 mb-4 transition-all ${
                dragOver 
                  ? 'border-[#6B35B8] bg-[#6B35B8]/10' 
                  : 'border-white/[0.06] hover:border-white/[0.10]'
              }`}
            >
              <Textarea
                ref={textareaRef}
                data-testid="universal-text-input"
                placeholder={recording ? 'Recording... tap the mic to stop' : transcribing ? 'Transcribing...' : 'Type, speak, paste, or drop anything (photos, PDFs, docs)...'}
                value={text}
                onChange={(e) => { setText(e.target.value); resizeTextarea(); }}
                onPaste={handlePaste}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && (text.trim() || files.length > 0)) { e.preventDefault(); handleSubmit(); } }}
                disabled={recording || transcribing}
                className="bg-transparent border-0 text-[#f0f0f5] placeholder:text-[#b0b4c0]/70 min-h-[80px] text-sm resize-none focus-visible:ring-0 p-0 overflow-y-auto"
                autoFocus
              />

              {files.length === 0 && !text && (
                <div className="text-center py-4">
                  <Upload size={24} className="mx-auto mb-2 text-[#6B35B8]/50" />
                  <p className="text-xs text-[#b0b4c0]">Drop photos, PDFs, or documents</p>
                </div>
              )}
            </div>

            {/* File previews */}
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {files.map((f, i) => {
                  const Icon = FILE_ICONS[f.type] || FILE_ICONS.default;
                  return (
                    <div key={i} className="relative group">
                      {f.type === 'image' && f.preview ? (
                        <img src={f.preview} alt="" className="h-16 w-16 object-cover rounded-lg border border-white/[0.06]" />
                      ) : (
                        <div className="h-16 w-16 rounded-lg border border-white/[0.06] bg-white/[0.05] flex flex-col items-center justify-center">
                          <Icon size={20} className="text-[#b0b4c0] mb-1" />
                          <span className="text-[8px] text-[#b0b4c0] uppercase">{f.type}</span>
                        </div>
                      )}
                      <button 
                        onClick={() => removeFile(i)} 
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white/[0.06] flex items-center justify-center text-[#b0b4c0] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={10} />
                      </button>
                      <p className="text-[9px] text-[#b0b4c0] truncate w-16 mt-1">{f.name}</p>
                    </div>
                  );
                })}
              </div>
            )}

            <p id="universal-input-desc" className="sr-only">Enter text or upload files for AI classification</p>

            {recording ? (
              /* Recording: full-width STOP bar */
              <button
                data-testid="universal-voice-btn"
                onClick={startRecording}
                className="w-full flex items-center justify-center gap-3 py-3 rounded-xl bg-[#E8450A]/15 border border-[#E8450A]/40 transition-all active:scale-[0.98]"
              >
                <div className="flex items-center gap-[3px] h-[18px]">
                  <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '60%', animationDelay: '0ms' }} />
                  <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
                  <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '40%', animationDelay: '300ms' }} />
                  <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '80%', animationDelay: '450ms' }} />
                </div>
                <span className="text-sm font-mono text-[#E8450A] font-medium tracking-wide">STOP</span>
                <div className="flex items-center gap-[3px] h-[18px]">
                  <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '80%', animationDelay: '100ms' }} />
                  <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '40%', animationDelay: '250ms' }} />
                  <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '100%', animationDelay: '350ms' }} />
                  <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '60%', animationDelay: '500ms' }} />
                </div>
              </button>
            ) : transcribing ? (
              /* Transcribing: full-width status bar */
              <div className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#9B6DD4]/10 border border-[#9B6DD4]/20">
                <Loader2 size={14} className="text-[#9B6DD4] animate-spin" />
                <span className="text-xs font-mono text-[#9B6DD4]/70">Transcribing...</span>
              </div>
            ) : (
              /* Idle: normal buttons */
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    data-testid="universal-upload-btn"
                    onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.05] border border-white/[0.06] text-[#b0b4c0] hover:text-[#f0f0f5] hover:border-[#6B35B8]/30 transition-colors text-xs"
                  >
                    <Upload size={14} />
                    Add Files
                  </button>
                  <button
                    data-testid="universal-voice-btn"
                    onClick={startRecording}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.05] border border-white/[0.06] text-[#b0b4c0] hover:text-[#f0f0f5] hover:border-[#6B35B8]/30 transition-colors text-xs"
                  >
                    <Mic size={16} /> Voice
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    multiple
                    className="hidden"
                    onChange={handleFileInput}
                  />
                </div>
                <Button
                  data-testid="universal-send-btn"
                  onClick={handleSubmit}
                  disabled={!text && files.length === 0}
                  className="bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full px-5 text-xs"
                >
                  <ArrowRight size={14} className="mr-1" />
                  Process {files.length > 0 ? `(${files.length + (text ? 1 : 0)})` : ''}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Processing step */}
        {step === 'processing' && (
          <div className="p-8 flex flex-col items-center gap-3" data-testid="universal-processing">
            <Loader2 size={32} className="text-[#6B35B8] animate-spin" />
            <p className="text-sm text-[#C8C8C8]">Zephyr is analyzing {files.length + (text ? 1 : 0)} input{files.length + (text ? 1 : 0) > 1 ? 's' : ''}...</p>
          </div>
        )}

        {/* Confirm step - multiple classifications */}
        {step === 'confirm' && classifications.length > 0 && (
          <div className="p-5 overflow-y-auto max-h-[70vh]" data-testid="universal-confirm-step">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-[#b0b4c0] font-mono tracking-wider uppercase">
                {classifications.length} item{classifications.length > 1 ? 's' : ''} classified
              </p>
            </div>

            {/* Classification cards */}
            <div className="space-y-3 mb-4">
              {classifications.map((classification, idx) => {
                const meta = APP_META[classification.app];
                const Icon = meta?.icon || Zap;
                return (
                  <div key={idx} className="bg-[#0a0a0f] rounded-xl p-4 border border-white/[0.06] relative group">
                    {/* Remove button */}
                    <button 
                      onClick={() => removeClassification(idx)}
                      className="absolute top-2 right-2 p-1 text-[#b0b4c0] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={14} />
                    </button>

                    {/* App selector */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex gap-1">
                        {Object.entries(APP_META).map(([key, appMeta]) => {
                          const active = classification.app === key;
                          const AppIcon = appMeta.icon;
                          return (
                            <button
                              key={key}
                              onClick={() => handleChangeApp(idx, key)}
                              className={`p-1.5 rounded transition-all ${
                                active 
                                  ? 'bg-white/10' 
                                  : 'text-[#b0b4c0]/50 hover:text-[#b0b4c0]'
                              }`}
                              style={active ? { color: appMeta.color } : undefined}
                              title={appMeta.label}
                            >
                              <AppIcon size={14} />
                            </button>
                          );
                        })}
                      </div>
                      <span className="text-xs font-medium" style={{ color: meta?.color }}>{meta?.label}</span>
                      <span className={`text-[10px] font-mono tracking-wider uppercase px-1.5 py-0.5 rounded ${
                        classification.action === 'update'
                          ? 'bg-amber-500/15 text-amber-400'
                          : 'bg-emerald-500/15 text-emerald-400'
                      }`}>
                        {classification.action === 'update' ? 'Update' : 'Create'}
                      </span>
                    </div>

                    {/* Title / target */}
                    {(classification.data?.title || classification.target_title) && (
                      <p className="text-sm text-[#f0f0f5] mb-2 font-medium">
                        {classification.data?.title || classification.target_title}
                      </p>
                    )}

                    {/* Explanation */}
                    <p className="text-xs text-[#b0b4c0] leading-relaxed">{classification.explanation}</p>
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex gap-3 sticky bottom-0 bg-white/[0.03] pt-2">
              <Button
                data-testid="universal-accept-btn"
                onClick={handleAcceptAll}
                disabled={applying}
                className="flex-1 bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full text-xs"
              >
                {applying ? (
                  <><Loader2 size={14} className="mr-1 animate-spin" /> Saving...</>
                ) : (
                  <><Check size={14} className="mr-1" /> Accept All ({classifications.length})</>
                )}
              </Button>
              <Button
                data-testid="universal-discard-btn"
                variant="outline"
                onClick={onClose}
                className="border-white/[0.06] text-[#b0b4c0] hover:text-[#f0f0f5] rounded-full px-5 text-xs"
              >
                Discard
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
